import { useState, useMemo } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { AlertTriangle, CheckCircle, Info, ArrowUpDown } from "lucide-react";

const fmtINR = (v) => {
  if (v == null) return "-";
  const abs = Math.abs(v);
  if (abs >= 1_00_00_000) return `${(v / 1_00_00_000).toFixed(2)} Cr`;
  if (abs >= 1_00_000) return `${(v / 1_00_000).toFixed(1)} L`;
  return v.toLocaleString("en-IN");
};

const CF_ROWS = [
  { label: "Operating Cash Flow", key: "operating", bold: false },
  { label: "Investing Cash Flow", key: "investing", bold: false },
  { label: "Financing Cash Flow", key: "financing", bold: false },
  { label: "Net Cash Flow", key: "net", bold: true, highlight: true },
  { label: "Opening Balance", key: "opening_balance", bold: false },
  { label: "Closing Balance", key: "closing_balance", bold: true, highlight: true },
];

const RiskBadge = ({ level }) => {
  const map = {
    low: { label: "Low Risk", variant: "default", icon: CheckCircle, cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    medium: { label: "Medium Risk", variant: "secondary", icon: Info, cls: "bg-amber-100 text-amber-800 border-amber-200" },
    high: { label: "High Risk", variant: "destructive", icon: AlertTriangle, cls: "bg-red-100 text-red-800 border-red-200" },
  };
  const cfg = map[level] || map.low;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.cls} gap-1`} data-testid="liquidity-risk-badge">
      <Icon className="w-3 h-3" /> {cfg.label}
    </Badge>
  );
};

const CashFlowForecast = ({ cashFlow, forecast, endPeriod, comparisonData, comparisonPeriod }) => {
  const [sortOrder, setSortOrder] = useState("asc");

  const filteredCashFlow = useMemo(() => {
    if (!cashFlow || cashFlow.length === 0) return [];
    if (!endPeriod) return cashFlow;
    const endIdx = parseInt(endPeriod, 10);
    if (!isNaN(endIdx) && endIdx >= 1 && endIdx <= cashFlow.length) {
      return cashFlow.slice(0, endIdx);
    }
    return cashFlow;
  }, [cashFlow, endPeriod]);

  const displayCashFlow = useMemo(() => {
    if (!filteredCashFlow || filteredCashFlow.length === 0) return [];
    return sortOrder === "asc" ? [...filteredCashFlow] : [...filteredCashFlow].reverse();
  }, [filteredCashFlow, sortOrder]);

  const compItem = useMemo(() => {
    if (!comparisonData || comparisonData.length === 0 || !comparisonPeriod || comparisonPeriod === "none") return null;
    const idx = parseInt(comparisonPeriod, 10) - 1;
    return (idx >= 0 && idx < comparisonData.length) ? comparisonData[idx] : comparisonData[comparisonData.length - 1];
  }, [comparisonData, comparisonPeriod]);

  if (!cashFlow || cashFlow.length === 0 || filteredCashFlow.length === 0) return <p className="text-muted-foreground p-4">No data</p>;

  const chartData = [
    ...displayCashFlow.map((p) => ({ period: p.period, cash: p.closing_balance, type: "actual" })),
    ...(forecast?.forecast || []).map((f) => ({ period: f.period, cash: f.projected_cash, type: "forecast" })),
  ];

  return (
    <div className="space-y-4" data-testid="cash-flow-section">
      {/* Action Strip */}
      <div className="flex items-center justify-between gap-2 px-1">
        <span className="text-xs text-muted-foreground">
          Showing <strong>{displayCashFlow.length}</strong> actual periods ({sortOrder === "asc" ? "Chronological" : "Latest First"})
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1.5"
          onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
          data-testid="cf-sort-btn"
        >
          <ArrowUpDown className="w-3.5 h-3.5 text-primary" />
          Sort Periods: {sortOrder === "asc" ? "Oldest → Newest" : "Newest → Oldest"}
        </Button>
      </div>

      {/* Cash trend chart */}
      <div className="h-64 w-full min-w-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={(v) => fmtINR(v)} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => fmtINR(v)} />
            <Area
              type="monotone" dataKey="cash" name="Cash Balance"
              stroke="hsl(211,100%,32%)" fill="hsl(211,100%,32%)" fillOpacity={0.15}
              strokeWidth={2}
              strokeDasharray={(d) => d?.type === "forecast" ? "5 5" : "0"}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Cash Flow Table */}
      <div className="border rounded-lg overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 bg-background z-10 min-w-[200px]">Line Item</TableHead>
              {displayCashFlow.map((p) => (
                <TableHead key={p.period} className="text-right min-w-[110px]">{p.period}</TableHead>
              ))}
              {compItem && (
                <>
                  <TableHead className="text-right min-w-[125px] bg-muted/40 font-bold text-foreground">
                    Comp ({compItem.period})
                  </TableHead>
                  <TableHead className="text-right min-w-[110px] bg-muted/40 font-bold text-foreground">
                    Variance
                  </TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {CF_ROWS.map((row) => {
              const latestVal = displayCashFlow[displayCashFlow.length - 1]?.[row.key] ?? 0;
              const compVal = compItem?.[row.key] ?? 0;
              const variance = compItem ? latestVal - compVal : 0;

              return (
                <TableRow key={row.key} className={row.highlight ? "bg-primary/5" : ""} data-testid={`cf-row-${row.key}`}>
                  <TableCell className={`sticky left-0 bg-background z-10 ${row.bold ? "font-semibold" : ""} ${row.highlight ? "bg-primary/5" : ""}`}>
                    {row.label}
                  </TableCell>
                  {displayCashFlow.map((p) => (
                    <TableCell key={p.period} className={`text-right tabular-nums ${row.bold ? "font-semibold" : ""} ${p[row.key] < 0 ? "text-red-600" : ""}`}>
                      {fmtINR(p[row.key])}
                    </TableCell>
                  ))}
                  {compItem && (
                    <>
                      <TableCell className={`text-right tabular-nums bg-muted/20 ${row.bold ? "font-semibold" : ""}`}>
                        {fmtINR(compVal)}
                      </TableCell>
                      <TableCell
                        className={`text-right tabular-nums bg-muted/20 font-medium ${
                          variance > 0 ? "text-emerald-600" : variance < 0 ? "text-red-600" : ""
                        }`}
                      >
                        {variance > 0 ? "+" : ""}{fmtINR(variance)}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Liquidity Forecast */}
      {forecast && forecast.forecast && (
        <Card className="card-industrial" data-testid="liquidity-forecast">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Liquidity Forecast (3-Month Projection)</CardTitle>
              <RiskBadge level={forecast.risk_level} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {forecast.forecast.map((f) => (
                <div key={f.period} className="p-3 rounded-lg bg-secondary/50 text-center">
                  <p className="text-xs text-muted-foreground font-medium">{f.period}</p>
                  <p className={`text-lg font-bold mt-1 ${f.projected_cash < 0 ? "text-red-600" : ""}`}>
                    {fmtINR(f.projected_cash)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Op: {fmtINR(f.projected_operating)} | Inv: {fmtINR(f.projected_investing)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CashFlowForecast;
